import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import './PaymentVerification.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PaymentVerification = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPaymentDetails();
  }, [id]);

  const fetchPaymentDetails = async () => {
    try {
      const res = await fetch(`${API_BASE}/contracts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setContract(data.contract);
        
        const paymentRes = await fetch(`${API_BASE}/payments/contract/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const paymentData = await paymentRes.json();
        if (paymentData.success) {
          setPayment(paymentData.payment);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (approved) => {
    if (!approved && !rejectionReason) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/contracts/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contractId: id,
          approved,
          rejectionReason: approved ? '' : rejectionReason
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(approved ? '✅ Payment verified!' : '❌ Payment rejected');
        navigate('/admin/payment-verifications');
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error processing verification');
    } finally {
      setProcessing(false);
    }
  };

  const handleReleasePayment = async () => {
    if (window.confirm('Release payment to service provider?')) {
      setProcessing(true);
      try {
        const res = await fetch(`${API_BASE}/contracts/${id}/release-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        if (data.success) {
          alert('💰 Payment released to service provider!');
          fetchPaymentDetails();
        } else {
          alert(data.message);
        }
      } catch (error) {
        alert('Error releasing payment');
      } finally {
        setProcessing(false);
      }
    }
  };

  if (loading) return <div className="loading-spinner">Loading payment details...</div>;
  if (!contract) return <div className="error-message">Contract not found</div>;

  return (
    <div className="payment-verification-page">
      <div className="page-header">
        <h1><i className="fas fa-hand-holding-usd"></i> Payment Verification</h1>
        <p>Review and verify payment proofs</p>
      </div>

      <div className="verification-card">
        <div className="contract-info">
          <h3>Contract Information</h3>
          <div className="info-grid">
            <div><strong>Contract ID:</strong> {contract._id?.slice(-8)}</div>
            <div><strong>Vessel:</strong> {contract.vesselName}</div>
            <div><strong>Service:</strong> {contract.serviceType}</div>
            <div><strong>Owner:</strong> {contract.ownerName || 'Owner'}</div>
            <div><strong>Provider:</strong> {contract.vendorName || 'Provider'}</div>
            <div><strong>Amount:</strong> <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>${contract.totalAmount?.toLocaleString()}</span></div>
          </div>
        </div>

        <div className="payment-proof-section">
          <h3><i className="fas fa-receipt"></i> Payment Proof</h3>
          
          {contract.paymentProof?.imageUrl ? (
            <div className="proof-image">
              <img src={contract.paymentProof.imageUrl} alt="Payment Proof" style={{ maxWidth: '100%', borderRadius: '8px' }} />
              {contract.paymentProof.notes && (
                <div className="proof-notes">
                  <strong>Notes from owner:</strong>
                  <p>{contract.paymentProof.notes}</p>
                </div>
              )}
              <div className="proof-meta">
                <small>Uploaded on: {new Date(contract.paymentProof.uploadedAt).toLocaleString()}</small>
              </div>
            </div>
          ) : (
            <p>No payment proof uploaded yet.</p>
          )}
        </div>

        {contract.status === 'payment_uploaded' && (
          <div className="verification-actions">
            <h3>Verify Payment</h3>
            <div className="form-group">
              <label>Rejection Reason (if rejecting)</label>
              <textarea
                rows="3"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Why is this payment proof being rejected?"
              />
            </div>
            <div className="action-buttons">
              <Button variant="danger" onClick={() => handleVerify(false)} disabled={processing}>❌ Reject Payment</Button>
              <Button variant="primary" onClick={() => handleVerify(true)} disabled={processing}>✅ Verify Payment</Button>
            </div>
          </div>
        )}

        {contract.status === 'work_completed' && (
          <div className="release-section">
            <h3>Release Payment to Provider</h3>
            <div className="release-info">
              <p><strong>Provider:</strong> {contract.vendorName}</p>
              <p><strong>Amount to Release:</strong> <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary)' }}>${contract.originalAmount?.toLocaleString()}</span></p>
              <p><small>Platform Fee (10%): ${contract.commissionAmount?.toLocaleString()} retained by SeaZex</small></p>
            </div>
            <Button variant="primary" onClick={handleReleasePayment} disabled={processing} fullWidth>💰 Release Payment to Provider</Button>
          </div>
        )}

        <div className="status-info">
          <h3>Current Status</h3>
          <div className="status-badge" style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: contract.status === 'payment_uploaded' ? 'rgba(59, 130, 246, 0.15)' : 
                        contract.status === 'work_completed' ? 'rgba(16, 185, 129, 0.15)' : 
                        'rgba(245, 158, 11, 0.15)',
            color: contract.status === 'payment_uploaded' ? '#3b82f6' : 
                   contract.status === 'work_completed' ? '#10b981' : '#f59e0b',
            borderRadius: '30px'
          }}>
            {contract.status === 'payment_uploaded' ? 'Awaiting Verification' : 
             contract.status === 'work_completed' ? 'Ready for Payment Release' : 
             contract.status}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentVerification;